import store from "../redux-store";
import { getExternalDiscounts } from "../redux-store/slices/DiscountSlice";
import { getClonedSaleDataFromReducer } from "../helpers/saleHelpers";
import { normaliseExternalOffers } from "./catalog";
import type { DiscountOffer } from "../promotions/types";

/**
 * The old discount core's public surface, and its one remaining imperative call.
 *
 * What is left of the old core is the pure rules (`applicability`, `catalog`, `provider`,
 * `billDiscount`, `release`) and the one store-reading file, `selectors`; the apply, verify and
 * pricing flow that used to dispatch from here is the session's now (`./session`, `./sources`).
 *
 * ⚠️ Every store read happens INSIDE a function. Nothing at module-eval time crosses into a
 * module that is in a cycle with the store (audit checks H/J).
 */

/* ── the offers list ───────────────────────────────────────────────────────────────────── */

/**
 * Fetch the partner offers available to this customer against the CURRENT sale.
 *
 * The answer is per-SALE, not per-customer: adding an item can change which offers qualify, so
 * the offers are handed to the callback rather than cached in a slice that would have to be
 * swept.
 *
 * ⚠️ Gate the call on `isExternalCouponProvider(provider?.metaId)` — a partner with no offers
 * leg has nothing to answer with. `useCustomerRewards` does.
 *
 * The offers arrive NORMALISED (`normaliseExternalOffers`): the API keys them under four
 * different names and the rest of the flow indexes by `discountId`.
 */
export function fetchExternalOffers(
  provider: { mid?: string; metaId?: string } | null | undefined,
  cb: (offers: DiscountOffer[]) => void,
): void {
  store.dispatch(
    getExternalDiscounts({
      data: {
        providerId: provider?.mid,
        metaId: provider?.metaId,
        sale: getClonedSaleDataFromReducer(),
      },
      callback: (response: any) => cb(normaliseExternalOffers(response?.discounts)),
    }),
  );
}

/* ── public surface ────────────────────────────────────────────────────────────────────── */

// The vocabulary, the bill entry and the partner registry are the shared base's since R2.4a
// (`packages/core/promotions`): import them from there, never through this folder.

export * from "./types";
export * from "./constants";
export * from "./provider";
export * from "./applicability";
export * from "./catalog";
export * from "./billDiscount";
export * from "./selectors";
// the rule lives in the shared base since R2.8a; re-exported here only for v1, whose `helpers/discountHelper` reaches it
// through this barrel until v1 goes (rule 18)
export * from "../promotions/itemLevelDiscounts";
